<script setup lang="ts">
/**
 * Body of /how-to-write-a-resume.
 *
 * Order is deliberate: the whole answer in one short paragraph at the top (the
 * passage an AI answer engine can lift), then the definition, then the seven
 * steps, then a finished example, then the two adjacent cases. The tool is
 * offered inside the steps where it is actually relevant, not as a banner.
 *
 * The step ids come from constants/guidePages so the page can build the HowTo
 * graph from exactly the steps rendered here.
 */
import { RESUME_STEP_IDS } from '~/constants/guidePages'

const { t } = useI18n()

const steps = RESUME_STEP_IDS
const bullets = ['retail', 'dev', 'teacher'] as const
const noExperience = ['coursework', 'parttime', 'volunteer', 'built'] as const

/** Multi-line strings live in one key each so the copy stays whole in i18n. */
const headerExample = computed(() => t('resumeGuide.steps.items.header.example').split('\n'))

const example = computed(() => {
  const jobs = ['acme', 'brightline', 'retail'] as const
  const bulletKeys = { acme: ['b1', 'b2', 'b3', 'b4'], brightline: ['b1', 'b2'], retail: ['b1'] } as const
  return jobs.map(id => ({
    id,
    role: t(`resumeGuide.example.body.jobs.${id}.role`),
    dates: t(`resumeGuide.example.body.jobs.${id}.dates`),
    points: bulletKeys[id].map(key => t(`resumeGuide.example.body.jobs.${id}.${key}`)),
  }))
})
</script>

<template>
  <div class="sofax">
    <!-- The answer, before anything else on the page -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space-sm">
        <div class="shell">
          <div class="mx-auto max-w-3xl rounded-[0.625rem] border-l-4 border-accent bg-surface p-[1.875rem] dark:bg-gray-800">
            <div class="mb-3 text-sm font-semibold uppercase tracking-wide text-muted dark:text-gray-400">
              {{ t('resumeGuide.short.label') }}
            </div>
            <p class="text-lg">{{ t('resumeGuide.short.text') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- What a resume is; answers the definition queries before selling anything -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space-bottom">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('resumeGuide.what.eyebrow')"
            :title="t('resumeGuide.what.title')"
            width="max-w-4xl"
          />
          <div class="mx-auto max-w-3xl">
            <p class="text-lg">{{ t('resumeGuide.what.p1') }}</p>
            <p class="mt-6 text-lg">{{ t('resumeGuide.what.p2') }}</p>
            <p class="mt-6 text-lg">{{ t('resumeGuide.what.p3') }}</p>
            <p class="mt-6">
              <LocaleLink to="/blog/resume-vs-cv-understanding-the-differences-and-when-to-use-each" class="link">
                {{ t('resumeGuide.what.cvLink') }} →
              </LocaleLink>
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- The procedure itself -->
    <section class="surface-alt">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('resumeGuide.steps.eyebrow')"
            :title="t('resumeGuide.steps.title')"
            :text="t('resumeGuide.steps.lead')"
            width="max-w-4xl"
          />

          <ol class="mx-auto flex max-w-4xl flex-col gap-6">
            <li
              v-for="(step, index) in steps"
              :id="`step-${index + 1}`"
              :key="step"
              class="rounded-[0.625rem] bg-white p-[1.875rem] dark:bg-gray-800"
            >
              <div class="mb-5 flex items-start gap-5">
                <span
                  class="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-ink !text-base !font-bold !text-white dark:bg-white dark:!text-ink"
                  aria-hidden="true"
                >{{ index + 1 }}</span>
                <div class="flex-1">
                  <h3 class="!text-xl md:!text-2xl">{{ t(`resumeGuide.steps.items.${step}.title`) }}</h3>
                  <div class="mt-2 text-sm font-semibold text-accent">{{ t(`resumeGuide.steps.items.${step}.time`) }}</div>
                </div>
              </div>

              <p class="!mb-4 text-base">{{ t(`resumeGuide.steps.items.${step}.text`) }}</p>
              <p class="text-base text-muted dark:text-gray-400">{{ t(`resumeGuide.steps.items.${step}.detail`) }}</p>

              <!-- Step 2 hands the layout decision to the page that owns it -->
              <p v-if="step === 'layout'" class="mt-5">
                <LocaleLink to="/templates" class="link">{{ t('templatesPage.heading') }} →</LocaleLink>
              </p>

              <!-- The last step decides the length; the full answer has its own page -->
              <p v-if="step === 'finish'" class="mt-5">
                <LocaleLink to="/how-long-should-a-resume-be" class="link">{{ t('resumeGuide.steps.lengthLink') }} →</LocaleLink>
              </p>

              <!-- Step 3 shows the header rather than describing it -->
              <div v-if="step === 'header'" class="mt-6 rounded-[0.625rem] bg-surface p-5 dark:bg-gray-900">
                <div class="mb-2 text-sm font-semibold uppercase tracking-wide text-muted dark:text-gray-400">
                  {{ t('resumeGuide.steps.exampleLabel') }}
                </div>
                <p
                  v-for="(line, i) in headerExample"
                  :key="i"
                  class="text-base"
                  :class="i === 0 ? 'font-title text-lg font-bold text-ink dark:text-white' : ''"
                >
                  {{ line }}
                </p>
              </div>

              <!-- Step 4: the summary, shown as the rewrite it is -->
              <div v-if="step === 'summary'" class="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div class="rounded-[0.625rem] bg-surface p-5 dark:bg-gray-900">
                  <div class="mb-2 flex items-center gap-2 text-base font-semibold text-ink dark:text-white">
                    <i class="bi bi-x-circle shrink-0 text-lg text-muted" />
                    {{ t('templatesPage.intro.before') }}
                  </div>
                  <p class="text-base">{{ t('resumeGuide.steps.items.summary.before') }}</p>
                </div>
                <div class="rounded-[0.625rem] bg-accent-soft/20 p-5 dark:bg-accent/10">
                  <div class="mb-2 flex items-center gap-2 text-base font-semibold text-ink dark:text-white">
                    <i class="bi bi-check-circle-fill shrink-0 text-lg text-accent" />
                    {{ t('templatesPage.intro.after') }}
                  </div>
                  <p class="text-base">{{ t('resumeGuide.steps.items.summary.after') }}</p>
                </div>
              </div>

              <!-- Step 5 carries the three rewrites: the page's main evidence -->
              <div v-if="step === 'experience'" class="mt-8">
                <div class="display-heading display-heading-5 mb-5">{{ t('resumeGuide.steps.bulletsTitle') }}</div>
                <ul class="flex flex-col gap-4">
                  <li v-for="item in bullets" :key="item" class="rounded-[0.625rem] bg-surface p-5 dark:bg-gray-900">
                    <!-- line-through sits on the text span only: an ancestor's
                         text-decoration is drawn over descendants and a child
                         cannot switch it back off, so the icon would be struck
                         through too. -->
                    <p class="!mb-3 flex items-start gap-3 text-base text-muted dark:text-gray-400">
                      <i class="bi bi-x-circle mt-0.5 shrink-0 text-lg" />
                      <span class="line-through">{{ t(`resumeGuide.steps.bullets.${item}.before`) }}</span>
                    </p>
                    <p class="flex items-start gap-3 text-base">
                      <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-lg text-accent" />
                      <span>{{ t(`resumeGuide.steps.bullets.${item}.after`) }}</span>
                    </p>
                  </li>
                </ul>
                <p class="mt-5 text-base text-muted dark:text-gray-400">{{ t('resumeGuide.steps.bulletsNote') }}</p>
                <p class="mt-3">
                  <LocaleLink to="/resume-synonyms" class="link">{{ t('resumeGuide.steps.synonymsLink') }} →</LocaleLink>
                </p>

                <div class="mt-8">
                  <SofaxBtn to="/free-cv-builder" :label="t('resumeGuide.steps.cta')" variant="accent" />
                  <span class="mt-4 block text-sm text-muted dark:text-gray-400">{{ t('resumeGuide.steps.ctaNote') }}</span>
                </div>
              </div>
            </li>
          </ol>
        </div>
      </div>
    </section>

    <!-- A finished resume, written out as text so the wording is the subject -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('resumeGuide.example.eyebrow')"
            :title="t('resumeGuide.example.title')"
            :text="t('resumeGuide.example.lead')"
            width="max-w-4xl"
          />

          <article class="mx-auto max-w-3xl rounded-[0.625rem] border border-line p-8 sm:p-10 dark:border-gray-700">
            <div class="display-heading display-heading-3">{{ t('resumeGuide.example.body.name') }}</div>
            <div class="mt-1 text-lg font-semibold text-accent">{{ t('resumeGuide.example.body.title') }}</div>
            <p class="mt-3 text-base text-muted dark:text-gray-400">{{ t('resumeGuide.example.body.contact') }}</p>

            <div class="mt-8 font-title text-base font-bold uppercase tracking-widest text-ink dark:text-white">{{ t('resumeGuide.example.body.summaryLabel') }}</div>
            <p class="mt-3 text-base">{{ t('resumeGuide.example.body.summary') }}</p>

            <div class="mt-8 font-title text-base font-bold uppercase tracking-widest text-ink dark:text-white">{{ t('resumeGuide.example.body.experienceLabel') }}</div>
            <div v-for="job in example" :key="job.id" class="mt-5">
              <div class="flex flex-wrap items-baseline justify-between gap-2">
                <span class="font-title font-bold text-ink dark:text-white">{{ job.role }}</span>
                <span class="text-sm text-muted dark:text-gray-400">{{ job.dates }}</span>
              </div>
              <ul class="mt-2 flex flex-col gap-1">
                <li v-for="(point, i) in job.points" :key="i" class="flex items-start gap-2 text-base">
                  <span aria-hidden="true">•</span>
                  <span>{{ point }}</span>
                </li>
              </ul>
            </div>

            <div class="mt-8 font-title text-base font-bold uppercase tracking-widest text-ink dark:text-white">{{ t('resumeGuide.example.body.skillsLabel') }}</div>
            <p class="mt-3 text-base">{{ t('resumeGuide.example.body.skills') }}</p>

            <div class="mt-8 font-title text-base font-bold uppercase tracking-widest text-ink dark:text-white">{{ t('resumeGuide.example.body.educationLabel') }}</div>
            <p class="mt-3 text-base">{{ t('resumeGuide.example.body.education') }}</p>

            <div class="mt-8 font-title text-base font-bold uppercase tracking-widest text-ink dark:text-white">{{ t('resumeGuide.example.body.languagesLabel') }}</div>
            <p class="mt-3 text-base">{{ t('resumeGuide.example.body.languages') }}</p>
          </article>

          <p class="mx-auto mt-4 max-w-3xl text-sm text-muted dark:text-gray-400">{{ t('resumeGuide.example.caption') }}</p>

          <div class="mt-10 text-center">
            <SofaxBtn to="/templates" :label="t('resumeGuide.example.cta')" variant="dark" />
          </div>
        </div>
      </div>
    </section>

    <!-- No experience yet: same layout, different evidence -->
    <section class="surface-alt">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('resumeGuide.noExperience.eyebrow')"
            :title="t('resumeGuide.noExperience.title')"
            :text="t('resumeGuide.noExperience.lead')"
            width="max-w-4xl"
          />

          <ul class="mx-auto flex max-w-3xl flex-col gap-4">
            <li v-for="item in noExperience" :key="item" class="flex items-start gap-4">
              <SofaxIconTile icon="bi-check-lg" round size="sm" />
              <p class="pt-2 text-base">{{ t(`resumeGuide.noExperience.items.${item}`) }}</p>
            </li>
          </ul>

          <p class="mt-8 text-center">
            <LocaleLink to="/blog/how-to-write-your-first-resume-with-no-work-experience" class="link">
              {{ t('resumeGuide.noExperience.link') }} →
            </LocaleLink>
          </p>
        </div>
      </div>
    </section>

    <!-- Where to write it; the deep comparison stays on /templates -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <div class="mx-auto max-w-3xl text-center">
            <div class="eyebrow mb-5">{{ t('resumeGuide.where.eyebrow') }}</div>
            <h2 class="!text-3xl md:!text-[2.5rem]">{{ t('resumeGuide.where.title') }}</h2>
            <p class="mt-5 text-lg">{{ t('resumeGuide.where.p1') }}</p>
            <p class="mt-5 text-lg">{{ t('resumeGuide.where.p2') }}</p>
            <p class="mt-6">
              <LocaleLink to="/templates" class="link">{{ t('resumeGuide.where.compareLink') }} →</LocaleLink>
            </p>
            <div class="mt-8">
              <SofaxBtn to="/free-cv-builder" :label="t('resumeGuide.where.cta')" variant="accent" />
            </div>
            <p class="mt-6">
              <LocaleLink to="/how-to-prepare-for-an-interview" class="link">{{ t('resumeGuide.where.interviewLink') }} →</LocaleLink>
            </p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
